import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const input = "h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm outline-none focus:border-[#0066FF]";
const STORE_FIELDS = [["brand", "Nome commerciale"], ["company_name", "Denominazione impresa"], ["holder", "Titolare"], ["vat", "Partita IVA"], ["rea", "REA"], ["cf", "Codice fiscale"], ["pec", "PEC"], ["address", "Sede"], ["email", "Email"], ["phone", "Cellulare"], ["hours", "Orari assistenza"], ["returns_address", "Indirizzo resi"]];
const SHIP_FIELDS = [["areas", "Aree servite"], ["carriers", "Corrieri utilizzati"], ["preparation_time", "Tempi di preparazione"], ["transit_time", "Tempi stimati di trasporto"], ["delay_notice", "Avviso ritardi (mostrato se compilato)"]];

export default function AdminShipping() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  if (!s) return <div className="h-60 animate-pulse rounded-2xl bg-white/5" />;

  const methods = s.shipping.methods;
  const setShip = (patch) => setS({ ...s, shipping: { ...s.shipping, ...patch } });
  const setM = (i, patch) => setShip({ methods: methods.map((m, k) => (k === i ? { ...m, ...patch } : m)) });
  const save = async () => {
    try {
      await api.put("/admin/settings", { store: s.store, shipping: s.shipping, klarna: s.klarna });
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-4xl space-y-8" data-testid="admin-shipping-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-semibold">Spedizioni e negozio</h1>
        <button onClick={save} className="h-10 rounded-full bg-[#0066FF] px-6 text-sm font-medium" data-testid="settings-save-btn">Salva</button>
      </div>
      <section className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <h2 className="font-display font-semibold">Metodi di spedizione</h2>
        <p className="mt-1 text-xs text-slate-500">I prodotti con "spedizione gratuita" (impostata da Ecwid o nella scheda prodotto) non pagano la spedizione.</p>
        <div className="mt-4 space-y-3">
          {methods.map((m, i) => (
            <div key={m.id || i} className="grid items-center gap-2 sm:grid-cols-[1fr_110px_1fr_auto_auto]" data-testid={`shipping-method-row-${i}`}>
              <input className={input} value={m.name} onChange={(e) => setM(i, { name: e.target.value })} placeholder="Nome" />
              <input type="number" step="0.01" className={input} value={m.price} onChange={(e) => setM(i, { price: e.target.value })} placeholder="€" />
              <input className={input} value={m.days || ""} onChange={(e) => setM(i, { days: e.target.value })} placeholder="Tempi (es. 24/48h)" />
              <Switch checked={m.enabled} onCheckedChange={(v) => setM(i, { enabled: v })} />
              <button onClick={() => setShip({ methods: methods.filter((_, k) => k !== i) })} className="p-1 text-slate-500 hover:text-red-400" aria-label="Rimuovi"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={() => setShip({ methods: [...methods, { id: "", name: "", price: 0, days: "", enabled: true }] })} className="text-sm text-[#4D94FF]" data-testid="shipping-add-method">+ Aggiungi metodo</button>
        </div>
        <label className="mt-5 block max-w-xs text-xs text-slate-400">Spedizione gratuita sopra (€, vuoto = mai)
          <input type="number" className={input + " mt-1.5"} value={s.shipping.free_threshold ?? ""} onChange={(e) => setShip({ free_threshold: e.target.value === "" ? null : Number(e.target.value) })} data-testid="shipping-free-threshold" />
        </label>
        <p className="mt-4 text-xs text-slate-500">Per tariffe territoriali aggiungi un metodo dedicato (es. "Isole").</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {SHIP_FIELDS.map(([k, l]) => (
            <label key={k} className="text-xs text-slate-400">{l}
              <input className={input + " mt-1.5"} value={s.shipping[k] || ""} onChange={(e) => setShip({ [k]: e.target.value })} data-testid={`shipping-${k}`} />
            </label>
          ))}
        </div>
      </section>
      <section className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <h2 className="font-display font-semibold">Dati dell'attività</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {STORE_FIELDS.map(([k, l]) => (
            <label key={k} className="text-xs text-slate-400">{l}
              <input className={input + " mt-1.5"} value={s.store[k] || ""} onChange={(e) => setS({ ...s, store: { ...s.store, [k]: e.target.value } })} data-testid={`store-${k}`} />
            </label>
          ))}
        </div>
        <label className="mt-4 flex items-center gap-3 text-sm"><Switch checked={!!s.store.whatsapp_enabled} onCheckedChange={(v) => setS({ ...s, store: { ...s.store, whatsapp_enabled: v } })} data-testid="store-whatsapp-enabled" />Mostra pulsante WhatsApp (wa.me/393791192613) — attivare solo dopo verifica del numero</label>
      </section>
      <section className="rounded-2xl border border-white/10 bg-[#12141A] p-5">
        <h2 className="font-display font-semibold">Klarna</h2>
        <p className="mt-1 text-xs text-slate-500">Attiva il messaggio "Paga anche in 3 rate con Klarna" solo dopo l'abilitazione di Klarna in Stripe, impostando gli importi idonei.</p>
        <div className="mt-4 grid items-end gap-3 sm:grid-cols-3">
          <label className="flex items-center gap-3 text-sm"><Switch checked={!!s.klarna?.enabled} onCheckedChange={(v) => setS({ ...s, klarna: { ...s.klarna, enabled: v } })} data-testid="klarna-enabled" />Messaggio Klarna attivo</label>
          <label className="text-xs text-slate-400">Importo minimo €<input type="number" className={input + " mt-1.5"} value={s.klarna?.min ?? 0} onChange={(e) => setS({ ...s, klarna: { ...s.klarna, min: Number(e.target.value) } })} data-testid="klarna-min" /></label>
          <label className="text-xs text-slate-400">Importo massimo €<input type="number" className={input + " mt-1.5"} value={s.klarna?.max ?? 0} onChange={(e) => setS({ ...s, klarna: { ...s.klarna, max: Number(e.target.value) } })} data-testid="klarna-max" /></label>
        </div>
      </section>
      <section className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5 text-sm text-amber-200" data-testid="payments-settings-status">
        Pagamenti (Stripe Checkout, modalità test): <b>{s.payments_enabled ? "ATTIVI" : "NON ATTIVI per il pubblico"}</b>. Verranno attivati solo dopo la verifica dell'account Stripe e il collaudo.
      </section>
    </div>
  );
}
